import React, { useState, useEffect } from 'react';
import { Search, Eye, Users, CreditCard, Calendar, History, TrendingUp } from 'lucide-react';
import Card from '../ui/Card';
import Modal from '../ui/Modal';
import Table from '../ui/Table';

/**
 * Read-only subscription management for USER_MANAGER role
 * Replicates admin subscription management but without edit/cancel buttons
 * Shows only history button as requested
 */
const SubscriptionViewOnlyTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [creditHistory, setCreditHistory] = useState([]);
  const [creditSummary, setCreditSummary] = useState(null);
  const [totalStats, setTotalStats] = useState({
    totalUsers: 0,
    totalCredits: 0,
    trialUsers: 0,
    monthlyUsers: 0,
    annualUsers: 0
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const fetchSubscriptionSummary = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      
      console.log('🔍 DEBUG: Starting fetchSubscriptionSummary');
      console.log('🔍 DEBUG: Admin token exists:', !!adminToken);
      console.log('🔍 DEBUG: API_BASE_URL:', API_BASE_URL);
      
      if (!adminToken) {
        console.error('❌ No admin token available');
        return;
      }

      const url = `${API_BASE_URL}/api/admin/subscription/subscription-summary`;
      console.log('🔍 DEBUG: Fetching from URL:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 DEBUG: Response status:', response.status);
      console.log('🔍 DEBUG: Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error:', errorText);
        throw new Error(`Failed to fetch subscription summary: ${response.status}`);
      }

      const data = await response.json();
      setUsers(data);
      console.log('🔍 DEBUG: Users state set to:', data);
      
      // Calculate totals
      const stats = {
        totalUsers: data.length,
        totalCredits: data.reduce((sum, user) => sum + (user.credits_remaining || 0), 0),
        trialUsers: data.filter(user => user.current_plan_type === 'trial').length,
        monthlyUsers: data.filter(user => user.current_plan_type === 'monthly').length,
        annualUsers: data.filter(user => user.current_plan_type === 'annual').length
      };
      console.log('🔍 DEBUG: Calculated stats:', stats);
      setTotalStats(stats);
      
    } catch (error) {
      console.error('❌ Error fetching subscription summary:', error);
      console.error('❌ Error stack:', error.stack);
    } finally {
      setLoading(false);
      console.log('🔍 DEBUG: Loading set to false');
    }
  };

  useEffect(() => {
    console.log('🔍 DEBUG: SubscriptionViewOnlyTab useEffect triggered');
    fetchSubscriptionSummary();
  }, []);

  const filteredUsers = users.filter(user =>
    user.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  console.log('🔍 DEBUG: Component render - filteredUsers length:', filteredUsers.length);

  const getCreditHistory = async (userId) => {
    if (!userId) {
      console.error('No userId provided to getCreditHistory');
      return;
    }
    
    try {
      const adminToken = localStorage.getItem('admin_token');
      const historyUrl = `${API_BASE_URL}/api/admin/subscription/users/${userId}/credit-history?limit=50`;
      
      const response = await fetch(historyUrl, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const historyData = await response.json();
        setCreditHistory(historyData);
        
        // Also get credit summary
        const summaryUrl = `${API_BASE_URL}/api/admin/subscription/users/${userId}/credit-summary`;
        const summaryResponse = await fetch(summaryUrl, {
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          setCreditSummary(summaryData);
        }
        
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch credit history:', response.status, errorText);
        alert(`Failed to load credit history: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching credit history:', error);
      alert('Error loading credit history');
    }
  };

  const handleViewHistory = async (user) => {
    if (!user || !user.user_id) {
      console.error('Invalid user object or missing user_id:', user);
      alert('Error: Invalid user data');
      return;
    }
    
    setSelectedUser(user);
    await getCreditHistory(user.user_id);
    setShowCreditHistory(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getPlanTypeColor = (planType) => {
    switch (planType?.toLowerCase()) {
      case 'trial':
        return 'bg-yellow-100 text-yellow-800';
      case 'monthly':
        return 'bg-blue-100 text-blue-800';
      case 'annual':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'credit_grant':
      case 'plan_assignment':
        return 'text-green-600';
      case 'credit_deduction':
        return 'text-red-600';
      case 'credit_refund':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  if (loading) {
    console.log('🔍 DEBUG: Component rendering - loading state');
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!users || users.length === 0) {
    console.log('🔍 DEBUG: Component rendering - no users state');
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-yellow-800">
            No subscription data available. Check console for debugging information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Subscription Management (Read-Only)
        </h1>
        <p className="text-gray-600">
          View user subscriptions and credit information. As a User Manager, you can view but not modify subscription data.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-blue-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {totalStats.totalUsers}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CreditCard className="h-8 w-8 text-green-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Credits</p>
              <p className="text-2xl font-semibold text-gray-900">
                {totalStats.totalCredits.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-yellow-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Trial Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {totalStats.trialUsers}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Paid Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {totalStats.monthlyUsers + totalStats.annualUsers}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search users by email or name..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <div className="p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">User Subscriptions</h3>
          
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No users found</p>
            </div>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Email / Name</Table.Head>
                  <Table.Head>Plan Type</Table.Head>
                  <Table.Head style={{ textAlign: 'right' }}>Credits</Table.Head>
                  <Table.Head>Trial Expires</Table.Head>
                  <Table.Head>Assigned By</Table.Head>
                  <Table.Head>Actions</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredUsers.map((user, index) => (
                  <Table.Row key={user.user_id || index}>
                    <Table.Cell>
                      <div>
                        <div className="font-medium text-gray-900">{user.user_email}</div>
                        <div className="text-sm text-gray-500">{user.user_name}</div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPlanTypeColor(user.current_plan_type)}`}>
                        {user.current_plan_type || 'None'}
                      </span>
                    </Table.Cell>
                    <Table.Cell style={{ textAlign: 'right' }}>
                      <div>
                        <div className="font-medium text-gray-900">
                          {user.credits_remaining?.toLocaleString() || '0'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Used: {user.total_credits_used?.toLocaleString() || '0'}
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {!user.trial_expires_at ? (
                        <span className="text-gray-400">N/A</span>
                      ) : (
                        <span className={`text-sm ${
                          new Date(user.trial_expires_at) < new Date() 
                            ? 'text-red-600 font-medium' 
                            : 'text-gray-600'
                        }`}>
                          {formatDate(user.trial_expires_at)}
                          {new Date(user.trial_expires_at) < new Date() && (
                            <div className="text-xs text-red-500">Expired</div>
                          )}
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-gray-600">
                        {user.assigned_by || 'System'}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <button
                        onClick={() => handleViewHistory(user)}
                        className="text-blue-600 hover:text-blue-900 flex items-center gap-1 text-sm"
                      >
                        <History size={14} />
                        History
                      </button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>
      </Card>

      {/* Credit History Modal */}
      {showCreditHistory && selectedUser && (
        <Modal
          isOpen={showCreditHistory}
          onClose={() => {
            setShowCreditHistory(false);
            setSelectedUser(null);
            setCreditHistory([]);
            setCreditSummary(null);
          }}
          title={`Credit History - ${selectedUser.user_name || selectedUser.user_email}`}
          size="large"
        >
          <div className="space-y-6">
            {/* Credit Summary */}
            {creditSummary && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Credit Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Current Balance</p>
                    <p className="font-semibold text-lg text-gray-900">
                      {creditSummary.current_balance?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Granted</p>
                    <p className="font-medium text-green-600">
                      +{creditSummary.total_granted?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Used</p>
                    <p className="font-medium text-red-600">
                      -{creditSummary.total_used?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Refunded</p>
                    <p className="font-medium text-blue-600">
                      +{creditSummary.total_refunded?.toLocaleString() || '0'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Transaction History */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Transaction History</h3>
              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {creditHistory.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No transaction history available</p>
                  ) : (
                    creditHistory.map((transaction, index) => (
                      <div
                        key={index}
                        className="bg-white border border-gray-200 rounded-lg p-4 flex justify-between items-center"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium capitalize text-gray-900">
                              {transaction.transaction_type?.replace('_', ' ')}
                            </span>
                            <span className={`text-sm font-semibold ${getTransactionColor(transaction.transaction_type)}`}>
                              {transaction.amount > 0 ? '+' : ''}{transaction.amount?.toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {transaction.reason || 'No reason specified'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(transaction.timestamp)}
                          </p>
                        </div>
                        <div className="text-right text-sm">
                          <div className="text-gray-500">Balance</div>
                          <div className="font-medium">
                            {transaction.balance_before?.toLocaleString() || '0'} → {transaction.balance_after?.toLocaleString() || '0'}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-yellow-800 text-sm">
                <strong>Note:</strong> As a User Manager, you can view this information but cannot modify subscriptions, adjust credits, or cancel subscriptions. Contact a SuperAdmin for changes.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SubscriptionViewOnlyTab;